import {ReactElement, useEffect, useRef} from 'react'
import snsWebSdk from '@sumsub/websdk'

type SumsubError = {code?: string; error?: string; reason?: string}

type SumsubContainerProps = {
  token: string
  locale: string
  fetchToken: () => Promise<string>
  onMessage: (type: string, payload: any) => void
  onError: (error: SumsubError) => void
}

/** Runs the Sumsub WebSDK inside the `.vv-sumsub` container. The SDK starts once per token. */
function SumsubContainer({token, locale, fetchToken, onMessage, onError}: SumsubContainerProps): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null)
  // The handlers change on every render of the pop-up. The refs keep the SDK from starting again.
  const handlers = useRef({fetchToken, onMessage, onError})
  handlers.current = {fetchToken, onMessage, onError}

  useEffect(() => {
    if (!containerRef.current) return undefined

    try {
      const sdk = snsWebSdk
        .init(token, () => handlers.current.fetchToken())
        .withConf({lang: locale})
        .withOptions({addViewportTag: false, adaptIframeHeight: true})
        .on('idCheck.onError', (error) => handlers.current.onError(error))
        .onMessage((type, payload) => handlers.current.onMessage(type, payload))
        .build()
      sdk.launch(containerRef.current)

      return () => sdk.destroy()
    } catch (error: unknown) {
      handlers.current.onError({error: error instanceof Error ? error.message : 'Sumsub did not start'})
      return undefined
    }
  }, [token, locale])

  return <div ref={containerRef} className="vv-sumsub" data-testid="vv-sumsub" />
}

export {SumsubContainer}
export type {SumsubError}
